import { useEffect, useMemo, useState } from 'react';
import { scrollPageTo } from '@/lib/scroll';
import { Link, useSearchParams } from 'react-router';
import { Ban, Check, ChevronLeft, ChevronRight, Moon, Settings, Sparkles, X } from 'lucide-react';
import { addDays, dayKeyIn, fmtTime, hijriOn, timesFor } from '@shared/prayer';
import { FAST_RULES, fastingInfo, upcomingFasts, type FastDayInfo } from '@shared/fasting';
import { useProgress, useSettings } from '@/lib/store';
import type { FastReason } from '@/lib/types';
import { Card, IconButton, LargeTitle, Row, Section, Sheet, haptic } from '@/components/ui';
import { dayLabel } from '@/features/prayer/Prayer';
import { WorshipSwitch } from '@/features/prayer/WorshipSwitch';
import { effectiveFast, hijriYearStats, qadaSummary, whiteDaysThisMonth } from './fastMetrics';
import { PeriodBanner } from './PeriodControls';
import { CrescentIcon, LanternIcon } from '@/components/islamic/Ornaments';

const REASONS: { value: FastReason; label: string }[] = [
  { value: 'illness', label: 'Illness' },
  { value: 'travel', label: 'Travelling' },
  { value: 'period', label: 'Period / nifās' },
  { value: 'pregnancy', label: 'Pregnancy or nursing' },
  { value: 'other', label: 'Other' },
];
const REASON_LABEL: Record<string, string> = { illness: 'illness', travel: 'travel', period: 'period', pregnancy: 'pregnancy/nursing', other: 'other', unspecified: 'no reason given' };

const hijriLabel = (i: FastDayInfo) => (i.hijri ? `${i.hijri.d} ${i.hijri.month} ${i.hijri.y}` : '');

export function FastingPage() {
  const { prayer, hijriOffset, hour12, gender } = useSettings((s) => s.settings);
  const fasts = useProgress((s) => s.fasts);
  const periods = useProgress((s) => s.periods);
  const markFast = useProgress((s) => s.markFast);
  const today = dayKeyIn(prayer.tz);
  const [params, setParams] = useSearchParams();
  const requested = params.get('day');
  const dayKey = requested && /^\d{4}-\d{2}-\d{2}$/.test(requested) && requested <= today ? requested : today;
  const setDay = (d: string) => { haptic(); setParams(d >= today ? {} : { day: d }, { replace: true }); };
  const [missSheet, setMissSheet] = useState(false);
  const [month, setMonth] = useState(today.slice(0, 7));
  useEffect(() => { setMonth(dayKey.slice(0, 7)); }, [dayKey]);

  const info = fastingInfo(dayKey, hijriOffset);
  const eff = effectiveFast(dayKey, fasts, periods, today, hijriOffset);
  const qada = useMemo(() => qadaSummary(fasts, periods, today, hijriOffset), [fasts, periods, today, hijriOffset]);
  const hy = hijriOn(today, hijriOffset)?.y ?? 0;
  const yearStats = useMemo(() => hijriYearStats(fasts, hy, hijriOffset), [fasts, hy, hijriOffset]);
  const white = whiteDaysThisMonth(fasts, today, hijriOffset);
  const upcoming = useMemo(() => upcomingFasts(addDays(today, 1), 30, hijriOffset).slice(0, 8), [today, hijriOffset]);
  const times = timesFor(prayer, dayKey);
  const todayInfo = fastingInfo(today, hijriOffset);

  const logFast = (kind: 'ramadan' | 'voluntary' | 'qada') => { haptic(15); markFast(dayKey, { status: 'fasted', kind }); };

  return (
    <div className="page-pad">
      <LargeTitle back="/prayer" title="Fasting" ar="الصيام" subtitle={todayInfo.hijri ? `${hijriLabel(todayInfo)} AH` : 'Ṣawm'}
        right={<IconButton label="Fasting settings" to="/settings/prayer"><Settings size={18} /></IconButton>} />
      <WorshipSwitch />

      {/* Day navigator */}
      {/* key: the header re-mounts (and flashes) whenever the day changes. */}
      <div key={dayKey} id="day-editor" className={`mx-2 flex items-center justify-between gap-2 px-2 py-1 pb-3 ${dayKey !== today ? 'day-flash' : ''}`} aria-live="polite">
        <button type="button" aria-label="Previous day" onClick={() => setDay(addDays(dayKey, -1))} className="press grid h-10 w-10 place-items-center rounded-full bg-fill"><ChevronLeft size={20} /></button>
        <div className="flex flex-col items-center">
          <label className="relative cursor-pointer text-[17px] font-bold">
            {dayLabel(dayKey, today)}
            <input type="date" aria-label="Choose a day" max={today} value={dayKey} onChange={(e) => e.target.value && setDay(e.target.value)} className="absolute inset-0 cursor-pointer opacity-0" />
          </label>
          <span className="text-[12px] text-muted">{hijriLabel(info)}{dayKey !== today && <> · <button type="button" className="font-semibold text-accent" onClick={() => setDay(today)}>Back to today</button></>}</span>
        </div>
        <button type="button" aria-label="Next day" disabled={dayKey === today} onClick={() => setDay(addDays(dayKey, 1))} className="press grid h-10 w-10 place-items-center rounded-full bg-fill disabled:opacity-30"><ChevronRight size={20} /></button>
      </div>

      <PeriodBanner dayKey={dayKey} today={today} context="fasting" />

      {/* The day */}
      <div className="px-4 pb-5">
        <Card className={`relative overflow-hidden p-5 ${info.ramadan ? 'bg-gradient-to-br from-gold/25 to-transparent' : info.rules.length ? 'bg-gradient-to-br from-accent/20 to-transparent' : ''}`}>
          {info.forbidden ? (
            <div className="flex items-start gap-3">
              <Ban className="mt-0.5 shrink-0 text-danger" size={22} />
              <div><div className="text-[19px] font-bold">{info.forbidden}</div><p className="text-[14px] text-muted">Fasting is not permitted today. ʿĪd mubārak!</p></div>
            </div>
          ) : (
            <>
              <div className="pointer-events-none absolute -right-2 -top-2 text-gold/25" aria-hidden>{info.ramadan ? <LanternIcon size={96} strokeWidth={1.2} /> : <CrescentIcon size={84} strokeWidth={1.2} />}</div>
              <div className="relative flex flex-wrap gap-2">
                {info.rules.length ? info.rules.map((r) => (
                  <span key={r.id} className={`rounded-full px-3 py-1 text-[13px] font-semibold ${r.strength === 'fard' ? 'bg-gold text-black' : r.strength === 'sunnah' ? 'bg-accent text-accent-ink' : 'bg-fill text-ink'}`}>
                    {r.label} <span className="font-arabic font-normal">· {r.ar}</span>
                  </span>
                )) : <span className="text-[15px] text-muted">No particular fast today — any day is good for a voluntary or make-up fast.</span>}
              </div>
              {info.rules[0] && <p className="mt-2 text-[13px] leading-snug text-muted">{info.rules[0].note}</p>}
              <div className="mt-3 flex gap-4 text-[13px] text-muted">
                <span>Suḥūr ends <b className="text-ink">{fmtTime(times.fajr, prayer.tz, hour12)}</b></span>
                <span>Ifṭār <b className="text-ink">{fmtTime(times.maghrib, prayer.tz, hour12)}</b></span>
              </div>

              <div className="mt-4">
                {eff?.status === 'fasted' ? (
                  <div className="flex items-center justify-between gap-3 rounded-2xl bg-accent/15 p-3">
                    <span className="flex items-center gap-2 font-semibold"><Check size={18} className="text-accent" /> Fasted{eff.kind === 'qada' ? ' · make-up' : eff.kind === 'ramadan' ? '' : ' · voluntary'}</span>
                    <button type="button" onClick={() => markFast(dayKey, null)} className="press text-[13px] font-semibold text-muted">Undo</button>
                  </div>
                ) : eff?.status === 'missed' ? (
                  <div className="flex items-center justify-between gap-3 rounded-2xl bg-[color-mix(in_srgb,var(--st-missed)_14%,transparent)] p-3">
                    <span className="flex items-center gap-2 font-semibold"><X size={18} className="text-[var(--st-missed)]" /> Not fasted · {REASON_LABEL[eff.reason ?? 'unspecified']}{eff.derived ? ' (from your period log)' : ''}</span>
                    {!eff.derived && <button type="button" onClick={() => markFast(dayKey, null)} className="press text-[13px] font-semibold text-muted">Undo</button>}
                  </div>
                ) : info.ramadan ? (
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => logFast('ramadan')} className="press rounded-2xl bg-accent py-3 font-semibold text-accent-ink">I fasted</button>
                    <button type="button" onClick={() => setMissSheet(true)} className="press rounded-2xl bg-fill py-3 font-semibold">I didn’t fast</button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => logFast('voluntary')} className="press rounded-2xl bg-accent py-3 font-semibold text-accent-ink">Voluntary fast</button>
                    <button type="button" onClick={() => logFast('qada')} disabled={qada.remaining === 0} className="press rounded-2xl bg-fill py-3 font-semibold disabled:opacity-40">Make-up fast</button>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Ramadan make-up */}
      <Section title="Ramadan make-up (qaḍāʾ)" footer={qada.totalMissed ? 'Log a make-up fast on any day outside Ramadan and the Eid days. It is taken off your oldest missed Ramadan first.' : 'Missed Ramadan fasts you log (or days excused by your period log) appear here so you know how many to make up.'}>
        <div className="flex items-center gap-4 p-4">
          <div className="grid h-16 w-16 shrink-0 place-items-center rounded-2xl bg-fill">
            <span className="text-[28px] font-bold tabular-nums leading-none">{qada.remaining}</span>
          </div>
          <div className="min-w-0">
            <div className="text-[17px] font-semibold">{qada.remaining === 0 ? (qada.totalMissed ? 'All made up — alḥamdulillāh' : 'Nothing to make up') : `${qada.remaining} fast${qada.remaining === 1 ? '' : 's'} to make up`}</div>
            <div className="text-[13px] text-muted">{qada.totalMissed} missed · {qada.madeUp} made up</div>
          </div>
        </div>
        {qada.years.map((y) => (
          <div key={y.hijriYear} className="row">
            <Moon size={16} className="text-gold" />
            <span className="min-w-0 flex-1">
              <span className="block font-medium">Ramadan {y.hijriYear} AH</span>
              <span className="block text-[12px] text-muted">{Object.entries(y.byReason).map(([r, n]) => `${n} ${REASON_LABEL[r]}`).join(' · ')}</span>
            </span>
            <span className="text-right text-[14px] tabular-nums text-muted">{y.remaining ? `${y.remaining} left` : 'done ✓'}<br /><span className="text-[11px]">{y.madeUp}/{y.missed} made up</span></span>
          </div>
        ))}
      </Section>

      {/* This year */}
      <Section title={`This year · ${hy} AH`}>
        <Row icon={<Sparkles size={18} />} label="Voluntary fasts" detail={String(yearStats.voluntary)} />
        <Row label="Six of Shawwāl" detail={`${yearStats.shawwal} / 6`} />
        <Row label="White days this month" detail={`${white} / 3`} />
        <Row label="Mondays & Thursdays" detail={String(yearStats.monThu)} />
      </Section>

      <FastCalendar month={month} setMonth={setMonth} today={today} selected={dayKey}
        onOpen={(d) => { setDay(d); scrollPageTo(0, true); }} />

      <Section title="Coming up" footer={hijriOffset ? `Hijri dates adjusted by ${hijriOffset > 0 ? '+' : ''}${hijriOffset} day(s).` : 'Hijri dates use the Umm al-Qurā calendar. If your community follows a local moon sighting, adjust it in Settings → Prayer times → Hijri date.'}>
        {upcoming.map((u) => (
          <div key={u.dayKey} className="row">
            <span className="w-[76px] shrink-0 text-[14px] font-semibold">{dayLabel(u.dayKey, today) === 'Today' ? 'Today' : new Date(`${u.dayKey}T12:00:00Z`).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' })}</span>
            <span className="min-w-0 flex-1 truncate text-[14px]">{u.rules.filter((r) => r.strength !== 'recommended').map((r) => r.label).join(' · ')}</span>
            <span className="text-[12px] text-muted">{u.hijri?.d} {u.hijri?.month.split(' ')[0]}</span>
          </div>
        ))}
      </Section>

      <Section title="Good days to fast">
        {Object.values(FAST_RULES).map((r) => (
          <div key={r.id} className="row items-start">
            <span className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${r.strength === 'fard' ? 'bg-gold' : r.strength === 'sunnah' ? 'bg-accent' : 'bg-faint'}`} />
            <span className="min-w-0 flex-1"><span className="block font-medium">{r.label} <span className="font-arabic text-muted">{r.ar}</span></span><span className="block text-[12px] leading-snug text-muted">{r.note}</span></span>
          </div>
        ))}
        <div className="row items-start"><Ban size={14} className="mt-1 shrink-0 text-danger" /><span className="text-[12px] leading-snug text-muted">Not permitted: the two Eids and the Days of Tashrīq (11–13 Dhul-Ḥijjah).</span></div>
      </Section>
      {gender == null && <p className="px-8 pb-6 text-center text-[13px] text-muted">Sisters can log periods so excused days are handled automatically — <Link to="/settings/profile" className="font-semibold text-accent">set your profile</Link>.</p>}

      <Sheet open={missSheet} onClose={() => setMissSheet(false)} title="Why didn’t you fast?">
        <p className="px-6 pb-3 text-[14px] text-muted">This day is added to your make-up count.</p>
        <Section>
          {REASONS.filter((r) => gender === 'female' || (r.value !== 'period' && r.value !== 'pregnancy')).map((r) => (
            <Row key={r.value} label={r.label} onClick={() => { haptic(12); markFast(dayKey, { status: 'missed', kind: 'ramadan', reason: r.value }); setMissSheet(false); }} />
          ))}
        </Section>
      </Sheet>
    </div>
  );
}

function FastCalendar({ month, setMonth, today, onOpen, selected }: { month: string; setMonth: (m: string) => void; today: string; onOpen: (d: string) => void; selected: string }) {
  const hijriOffset = useSettings((s) => s.settings.hijriOffset);
  const fasts = useProgress((s) => s.fasts);
  const periods = useProgress((s) => s.periods);
  const [y, m] = month.split('-').map(Number);
  const start = `${month}-01`;
  const len = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const lead = (new Date(`${start}T12:00:00Z`).getUTCDay() + 6) % 7;
  const cells = [...Array(lead).fill(null), ...Array.from({ length: len }, (_, i) => addDays(start, i))];
  const shift = (d: number) => { const t = new Date(Date.UTC(y, m - 1 + d, 1)); setMonth(`${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}`); };
  const label = new Date(`${start}T12:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return (
    <Section title="Calendar" footer="Gold ring: Ramadan · green dot: sunnah fast day · filled: fasted · red: not fasted · ✕: fasting not permitted.">
      <div className="p-4">
        <div className="flex items-center justify-between pb-3">
          <button type="button" aria-label="Previous month" onClick={() => shift(-1)} className="press grid h-8 w-8 place-items-center rounded-full bg-fill"><ChevronLeft size={16} /></button>
          <span className="font-semibold">{label}</span>
          <button type="button" aria-label="Next month" onClick={() => shift(1)} className="press grid h-8 w-8 place-items-center rounded-full bg-fill"><ChevronRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1.5 text-center text-[11px] font-medium text-muted">{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i}>{d}</span>)}</div>
        <div className="mt-1.5 grid grid-cols-7 gap-1.5">
          {cells.map((d, i) => {
            if (!d) return <span key={`x${i}`} />;
            const info = fastingInfo(d, hijriOffset);
            const eff = effectiveFast(d, fasts, periods, today, hijriOffset);
            const sunnah = info.rules.some((r) => r.strength === 'sunnah');
            const bg = eff?.status === 'fasted' ? 'var(--accent)' : eff?.status === 'missed' ? 'color-mix(in srgb, var(--st-missed) 30%, transparent)' : 'var(--fill)';
            return (
              <button key={d} type="button" disabled={d > today} onClick={() => onOpen(d)} aria-pressed={d === selected}
                aria-label={`${d}${info.hijri ? `, ${info.hijri.d} ${info.hijri.month}` : ''}${info.rules.length ? `, ${info.rules.map((r) => r.label).join(', ')}` : ''}${info.forbidden ? `, ${info.forbidden}` : ''}${eff ? `, ${eff.status}` : ''}`}
                className={`press relative flex aspect-square flex-col items-center justify-center rounded-[9px] text-[12px] font-semibold disabled:opacity-50 ${info.ramadan ? 'ring-2 ring-gold' : ''} ${d === selected ? 'outline outline-[3px] outline-offset-2 outline-ink' : d === today ? 'outline outline-2 outline-offset-1 outline-accent' : ''}`}
                style={{ background: bg, color: eff?.status === 'fasted' ? 'var(--accent-ink)' : undefined }}>
                <span>{Number(d.slice(8))}</span>
                <span className="text-[9px] font-medium opacity-70">{info.forbidden ? '✕' : info.hijri?.d}</span>
                {sunnah && eff?.status !== 'fasted' && <span className="absolute right-1 top-1 h-1.5 w-1.5 rounded-full bg-accent" />}
              </button>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

